<template>
  <div class="gw-planning-compliance">
    <div ref="tableScroller" class="gw-planning-compliance__wrapper">
      <canvas v-if="displayChart" ref="chart" class="f-h f-w" />
      <table v-else class="gw-planning-compliance__table">
        <thead class="gw-planning-compliance__table__header">
          <tr class="gw-planning-compliance__table__row">
            <th class="gw-planning-compliance__table__header__item">{{ $t("message.PLANNING") }}</th>
            <th
              v-for="label in tableLabels"
              :key="label.key"
              class="gw-planning-compliance__table__header__item gw-planning-compliance__cell"
            >
              {{ label.displayLabel }}
            </th>
          </tr>
        </thead>
        <tbody class="gw-planning-compliance__table__body">
          <tr
            v-for="planning in tableRows"
            :key="planning.key"
            class="gw-planning-compliance__table__row"
            :class="{ 'gw-planning-compliance__table__row--borderless': planning.borderless }"
          >
            <th class="gw-planning-compliance__table__header__item">
              <div
                v-if="!planning.templateNameHidden"
                v-quick-title="`${planning.name} (${planning.teamName})`"
                class="gw-planning-compliance__planning-name"
              >
                {{ `${planning.name} (${planning.teamName})` }}
              </div>
            </th>
            <td
              v-for="label in tableLabels"
              :key="label.key"
              class="gw-planning-compliance__table__data"
              :style="cellStyle"
            >
              <div
                v-if="planning.perDate[label.key]"
                class="gw-planning-compliance__cell__wrapper__point"
                :class="{ 'gw-planning-compliance__cell__wrapper__point--ok': planning.perDate[label.key].ok }"
              >
                <div :style="{ background: planning.perDate[label.key].background }" />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="gw-planning-compliance__switcher-wrapper">
      <div class="gw-planning-compliance__switcher-wrapper__shadow">
        <Switcher
          class="gw-planning-compliance__switcher"
          :value="displayChart ? 1 : 0"
          :titles="[$t('message.TABLE'), $t('message.CHART')]"
          @input="displayChart = $event === 1"
        />
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Display a chart or a table indicating if the planned GW have been carried out or not each day.
 *
 * @vue-prop {Array} teams -  Array of team ids to filter
 * @vue-prop {Array} templates -  Arrray of template ids to filter
 */

import { Chart } from 'chart.js';
import { cloneDeep, isEqual } from 'lodash';
// Components
import Switcher from '@/components/switcher.vue';
// Utils
import { getPlanningsBreakdown } from '@/utils/vhub-utils';
import { CL_ITEMS_CHART_TYPES } from '@/utils/gw-stats';
import { ChartHelper, formatConfig, valueKeyFormat } from '@/utils/indicator-utils';
import { getTextWidth, defaultFont, isWorkingDay } from '@/utils/main';
import { SColors } from '@/utils/constants';

export default {

  name: 'GwPlanningCompliance',

  components: {
    Switcher,
  },

  props: {
    type: {
      type: String,
      default: CL_ITEMS_CHART_TYPES[0].type,
    },
    teams: {
      type: Array,
      required: true,
    },
    templates: {
      type: Array,
      required: true,
    },
    range: {
      type: Object,
      required: true,
    },
    workingDays: {
      type: Array,
      default: () => [true, true, true, true, true, true, true],
    },
    hideNonWorkingDays: Boolean,
    displayConfig: {
      type: Object,
      required: true,
    },
    showTable: Boolean,
  },

  data() {
    return {
      displayChart: false,
      chartHelper: null,
      chart: null,
      loading: false,
      completedGws: [],
      gwPlannings: [],
      dateFormat: this.$t('minDateFormat'),
    };
  },

  computed: {
    /** Array: Date labels of the table header. All dates between range.start and range.end */
    tableLabels() {
      const { chartHelper, chartData: { chartHelperPoints } } = this;
      if (!chartHelperPoints) {
        return [];
      }
      const { labels } = chartHelper.getLabelsFromChartPoints(chartHelperPoints);
      return chartHelperPoints.map((point, idx) => ({
        displayLabel: labels[idx],
        key: point.period.start.format(valueKeyFormat),
        mDate: point.period.start,
      }));
    },
    /** Array of plannings for the compliance table */
    tableRows() {
      const tableRows = cloneDeep(this.filteredPlannings).sort((p1, p2) => p1.gw - p2.gw);
      const { datesPerPlanning, chartData: { chartHelperPoints } } = this;
      if (!chartHelperPoints) {
        return [];
      }
      const now = this.$moment();
      // Loop through 'template-team' sets and set the number of expected occurrences per date.
      // Then check if each of these plannings have been carried out.
      const notFirstOfTemplate = {};
      tableRows.forEach((planning, idx) => {
        if (notFirstOfTemplate[`${planning.gw}-${planning.team}`]) {
          this.$set(planning, 'templateNameHidden', true);
          this.$set(tableRows[idx - 1], 'borderless', true);
        } else {
          notFirstOfTemplate[`${planning.gw}-${planning.team}`] = true;
        }
        planning.perDate = {};
        const planningDates = datesPerPlanning[`${planning.id}`];
        if (planningDates) {
          chartHelperPoints.forEach(({ period }) => {
            const columnKey = period.start.format(valueKeyFormat);
            // Check if there was a missing field tour in the point period
            let nExpected = 0;
            let nExpectedDone = 0;
            let nExpectedLater = 0;
            const iStart = this.$moment(period.start);
            while (iStart.isSameOrBefore(period.end, 'd')) {
              const key = iStart.format(valueKeyFormat);
              if (planningDates[key] && isWorkingDay(iStart, this.workingDays)) {
                if (iStart.isAfter(now)) {
                  nExpectedLater++;
                } else {
                  nExpected++;
                }
                nExpectedDone += planningDates[key].carriedOut ? 1 : 0;
              }
              iStart.add(1, 'day');
            }
            let ok = false;
            let color = { r: 0, g: 0, b: 0, a: 0 };
            if (nExpected > 0) {
              ok = nExpectedDone === nExpected;
              color = ok ? SColors.indicatorsGreenLine : SColors.indicatorsRedLine;
            } else if (nExpectedLater > 0) {
              color = SColors.mediumGray;
            }
            planning.perDate[columnKey] = { ok, background: color };
          });
        }
      });
      return tableRows;
    },
    /** Object: Get breakdown of plannings per date within the range (expected) */
    planningsPerDate() {
      return getPlanningsBreakdown(this.filteredPlannings, this.completedGws, this.range).planningsPerDate;
    },
    /** Object: Get breakdown of dates per plannings */
    datesPerPlanning() {
      return getPlanningsBreakdown(this.filteredPlannings, this.completedGws, this.range).datesPerPlanning;
    },
    /** Object: CSS style of the cells of the table */
    cellStyle() {
      // Convert font size to pixels, otherwise measureText won't work (it would work if the canvas was on the DOM)
      // NOTE: Changing '0.85' value should imply a change in the CSS font-size too
      const fontSize = 0.85 * this.$store.getters.fontSize;
      const maxWidth = this.tableLabels.reduce((max, label) => {
        const lWidth = getTextWidth(label.displayLabel, `${fontSize}px "${defaultFont}"`);
        return Math.max(max, lWidth);
      }, 0);
      return {
        flex: `0 0 ${maxWidth}px`,
      };
    },
    /** Object: this.gwPlannings as an object where key is `<gw.template>-<gw.team>` */
    gwPerTemplateTeam() {
      return this.gwPlannings.reduce((all, planning) => ({
        ...all,
        [`${planning.gw}-${planning.team}`]: {
          id: planning.gw,
          name: planning.name,
          teamName: planning.teamName,
        },
      }), {});
    },
    /** Object Dataof the chart to pass to Chart.js */
    chartData() {
      if (this.loading || this.chartHelper === null) {
        return {};
      }
      const { chartHelper, workingDays, range, planningsPerDate } = this;
      const valuesDone = {};
      const valuesExpected = {};
      Object.keys(planningsPerDate).forEach((dateKey) => {
        valuesDone[dateKey] = { reported: dateKey, value: planningsPerDate[dateKey].nTotalDone || 0 };
        valuesExpected[dateKey] = { reported: dateKey, value: planningsPerDate[dateKey].nTotalExpected || 0 };
      });
      const formattedConfig = formatConfig(this.displayConfig);
      // Let the chartHelper remove the non working days if the option is set
      const fakeIndic = { workingDays: this.hideNonWorkingDays ? workingDays : undefined };
      // Build points according to the chart config
      const donePoints = chartHelper
        .buildCustomConfigChart(fakeIndic, valuesDone, formattedConfig, workingDays, range).points;
      const expectedPoints = chartHelper
        .buildCustomConfigChart(fakeIndic, valuesExpected, formattedConfig, workingDays, range).points;

      const expectedPointValues = expectedPoints.map(p => p.data || 0);
      const donePointValues = donePoints.map(p => p.data || 0);
      const notDonePointValues = donePoints.map((o, idx) => expectedPointValues[idx] - donePointValues[idx]);
      const notDonePercentages = notDonePointValues
        .map((val, idx) => (expectedPointValues[idx] === 0 ? 0 : val * 100 / expectedPointValues[idx]));
      const donePercentages = donePointValues
        .map((val, idx) => (expectedPointValues[idx] === 0 ? 0 : val * 100 / expectedPointValues[idx]));

      const isLine = this.type === CL_ITEMS_CHART_TYPES[0].type;
      const commonDataset = {
        type: this.type,
        borderWidth: isLine ? 2 : 0.1,
        tension: 0,
        pointBackgroundColor: '#ffffff',
        pointRadius: 2,
        pointHitRadius: 40,
        pointHoverRadius: 7,
        maxBarThickness: 8,
        borderRadius: 200,
      };
      return {
        datasets: [
          {
            ...commonDataset,
            values: donePointValues,
            label: this.$t('message.GW_OK_ITEMS'),
            backgroundColor: isLine ? SColors.indicatorsGreenBg : SColors.indicatorsGreenLine,
            borderColor: SColors.indicatorsGreenLine,
            pointBackgroundColor: SColors.indicatorsGreenLine,
            data: donePercentages,
            pointPeriods: donePoints.map(p => p.period),
            fill: 'start',
          },
          {
            ...commonDataset,
            values: notDonePointValues,
            label: this.$t('message.GW_OK_ITEMS'),
            backgroundColor: isLine ? SColors.indicatorsRedBg : SColors.indicatorsRedLine,
            borderColor: SColors.indicatorsRedLine,
            pointBackgroundColor: SColors.indicatorsRedLine,
            data: notDonePercentages,
            pointPeriods: donePoints.map(p => p.period),
            fill: '-1',
          },
        ],
        labels: chartHelper.getLabelsFromChartPoints(donePoints).labels,
        chartHelperPoints: donePoints,
      };
    },
    /** Object: PArams of the XHR to fetch completed GWs */
    fetchParams() {
      return {
        teams: this.teams,
        range: this.range,
      };
    },
    /** Object: Templates per team */
    gwTemplatesPerTeam() {
      return this.$store.state.gembaWalks.templatesPerTeam;
    },
    /** Array: All selected template objects */
    filteredTemplates() {
      const allTemplates = this.$store.getters['gembaWalks/allTemplates'];
      if (this.templates.length > 0) {
        return this.templates.filter(template => this.templates.includes(template.id));
      }
      return allTemplates;
    },
    /** Array: All selected template objects */
    filteredPlannings() {
      let filteredPlannings = cloneDeep(this.gwPlannings);
      if (this.templates.length > 0) {
        filteredPlannings = filteredPlannings.filter(planning => this.templates.includes(planning.gw));
      }
      if (this.teams.length > 0) {
        filteredPlannings = filteredPlannings.filter(planning => this.teams.includes(planning.team));
      }
      return filteredPlannings;
    },
  },

  watch: {
    fetchParams() {
      this.refresh();
    },
    chartData(newData) {
      if (newData.datasets instanceof Array && this.chart) {
        this.chart.data = newData;
        this.chart.update();
      }
    },
    tableLabels(newVal, oldVal) {
      if (!isEqual(newVal, oldVal)) {
        this.$nextTick(() => {
          if (this.$refs.tableScroller) {
            this.$refs.tableScroller.scrollLeft = 100000000;
          }
        });
      }
    },
    displayChart() {
      if (this.displayChart) {
        this.$nextTick(this.createChart);
      }
    },
  },

  created() {
    this.chartHelper = new ChartHelper();
  },

  mounted() {
    this.createChart();
    this.refresh();
  },

  methods: {
    resizeChart() {
      if (this.chart) {
        this.chart.resize();
      }
    },
    refresh() {
      // Fetch data
      this.loading = true;
      // Fetch completed GWs
      const completedPromise = new Promise((resolve, reject) => {
        this.$http.post('/server/gembaWalks/get.php', { ...this.fetchParams, completed: true })
          .then(({ data }) => {
            if (Array.isArray(data)) {
              this.completedGws = data;
              resolve();
            }
          })
          .catch(reject);
      });
      // Fetch GW plannings
      const planningsPromise = new Promise((resolve, reject) => {
        this.$http.post('/server/gembaWalks/get.php', this.fetchParams)
          .then(({ data }) => {
            if (Array.isArray(data)) {
              this.gwPlannings = data;
            }
          })
          .catch(reject);
      });
      Promise.all(completedPromise, planningsPromise)
        .then(() => {
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    createChart() {
      const canvas = this.$refs.chart;
      if (!canvas) {
        return;
      }
      if (this.chart) {
        this.chart.destroy();
      }
      const context = canvas.getContext('2d');
      this.chart = new Chart(context, {
        type: 'bar',
        data: this.chartData,
        options: {
          spanGaps: true,
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              stacked: true,
              beginAtZero: true,
              max: 103,
              ticks: {
                callback(label) {
                  return (label % 10 === 0) ? label : null;
                },
              },
              grid: {
                color: SColors.grayBgHover,
                borderDash: [3, 3],
                tickColor: 'rgba(0, 0, 0, 0)',
                borderColor: 'rgba(0, 0, 0, 0)',
              },
            },
            x: {
              stacked: true,
              grid: {
                drawTicks: false,
                color: 'transparent',
              },
            },
          },
          plugins: {
            tooltip: {
              callbacks: {
                label: (tooltipItem) => {
                  const doneDataset = this.chartData.datasets[0];
                  const notDoneDataset = this.chartData.datasets[1];
                  const dateIdx = tooltipItem.dataIndex;
                  const { start, end } = doneDataset.pointPeriods[dateIdx];
                  const nDone = doneDataset.values[dateIdx];
                  const nTotal = nDone + notDoneDataset.values[dateIdx];
                  const iStart = this.$moment(start);
                  let notDoneMsg = '';
                  while (iStart.isBefore(end) || iStart.isSame(end, 'd')) {
                    const dateKey = iStart.format(valueKeyFormat);
                    if (this.planningsPerDate[dateKey] && this.planningsPerDate[dateKey].expected) {
                      let dateMsg = '';
                      // Object where the key is '<gwTemplate>-<gwTeam>'
                      // and the value is the number of field tour not carried out
                      Object.entries(this.planningsPerDate[dateKey].expected).forEach(([tmpTeam, nExpected]) => {
                        const nNotDone = nExpected - this.planningsPerDate[dateKey].expectedDone[tmpTeam];
                        if (nNotDone > 0 && this.gwPerTemplateTeam[tmpTeam]) {
                          const { name, teamName } = this.gwPerTemplateTeam[tmpTeam];
                          dateMsg = `${dateMsg}${dateMsg === '' ? '' : '<br>'}- ${name} (${teamName})`;
                        }
                      });
                      if (dateMsg !== '') {
                        const fDate = iStart.format(this.dateFormat);
                        notDoneMsg = `${notDoneMsg}${notDoneMsg === '' ? '' : '<br>'}${fDate}<br>${dateMsg}`;
                      }
                    }
                    iStart.add(1, 'day');
                  }
                  if (notDoneMsg !== '') {
                    notDoneMsg = `<br>Missing field tour :<br>${notDoneMsg}`;
                  }
                  const perc = nDone * 100 / nTotal;
                  return `${nDone} / ${nTotal} (${perc.sToFixed(1)}%)<br>${notDoneMsg}`;
                },
              },
            },
          },
        },
      });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.gw-planning-compliance {
  $name-col-width: 10em;
  position: relative;

  &__switcher-wrapper {
    position: absolute;
    top: -2.7rem;
    right: 0.3rem;
    z-index: 1;
    padding-left: 3rem;
    overflow: hidden;

    &__shadow {
      background: white;
      box-shadow: 0px 0px 1.5rem 1.5rem white;
    }
  }

  &__switcher {
    height: 2rem;
  }

  &__table {
    white-space: nowrap;
    margin: 0;
    border: none;
    border-spacing: 0;
    table-layout: fixed;
    border-collapse: collapse;

    & thead .gw-planning-compliance__table__header__item {
      font-size: 0.9em;
      text-align: left;
      font-weight: normal;
      padding: 3px;
      position: sticky;
      top: 0;
      z-index: 1;
      width: 25vw;
      background: white;

      &:first-child {
        position: sticky;
        left: 0;
        z-index: 2;
      }

      &:not(:first-child) {
        text-align: center;
      }
    }

    & tbody .gw-planning-compliance__table__header__item {
      font-size: 0.9em;
      text-align: left;
      font-weight: normal;
      position: sticky;
      left: 0;
      background: white;
      z-index: 1;
    }

    &__row {
      border-bottom: 1px solid lightgrey;

      &--borderless {
        border-bottom: none;
      }
    }

    &__data {
      background: #fff;
      padding: 4px 5px;
      text-align: center;
    }
  }

  &__wrapper {
    height: 100%;
    overflow: auto;
  }

  &__rows {
    display: inline-block;
  }

  &__row {
    border-bottom: 1px solid lightgrey;
    display: flex;
    align-items: center;
    padding: 0.3em 0;
    min-height: 1.4em;
  }
  &__planning-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: $name-col-width;
    padding: 0.3em 0;
  }

  &__cell {
    padding: 0 0.3em;

    &__wrapper {
      width: 100%;

      &__point {
        text-align: center;
        width: 100%;

        div {
          display: inline-block;
          width: 8px;
          height: 8px;
          border-radius: 1em;
        }
      }
    }
  }
}
</style>
